import { isDayAfter, isDayBefore, isDayInRange, isSameDay } from './DateUtils';
import { isRangeOfDates } from './Helpers';

type DateRange = {
  from: Date;
  to: Date;
};

type ModifierRange = {
  after?: Date;
  before?: Date;
  daysOfWeek?: Array<number>;
};

type Modifier = Date | DateRange | ModifierRange | ((day: Date) => boolean);

/**
 * Return `true` if a date matches the specified modifier.
 *
 * @export
 * @param {Date} day
 * @param {Modifier | Array<Modifier>} modifier
 * @return {Boolean}
 */
export function dayMatchesModifier(day: Date, modifier: Modifier | Array<Modifier> | null | undefined): boolean {
  if (!modifier) {
    return false;
  }
  const arr = Array.isArray(modifier) ? modifier : [modifier];
  return arr.some((mod) => {
    if (!mod) {
      return false;
    }
    if (mod instanceof Date) {
      return isSameDay(day, mod);
    }
    if (isRangeOfDates(mod)) {
      return isDayInRange(day, mod);
    }
    // Type guard for ModifierRange
    if (typeof mod === 'object' && !Array.isArray(mod) && !(mod instanceof Date)) {
      const modRange = mod as ModifierRange;
      if (modRange.after && modRange.before && isDayAfter(modRange.before, modRange.after)) {
        return isDayAfter(day, modRange.after) && isDayBefore(day, modRange.before);
      }
      if (
        modRange.after &&
        modRange.before &&
        (isDayAfter(modRange.after, modRange.before) || isSameDay(modRange.after, modRange.before))
      ) {
        return isDayAfter(day, modRange.after) || isDayBefore(day, modRange.before);
      }
      if (modRange.after) {
        return isDayAfter(day, modRange.after);
      }
      if (modRange.before) {
        return isDayBefore(day, modRange.before);
      }
      if (modRange.daysOfWeek) {
        return modRange.daysOfWeek.some((dayOfWeek: number) => day.getDay() === dayOfWeek);
      }
    }
    if (typeof mod === 'function') {
      return mod(day);
    }
    return false;
  });
}

/**
 * Return the modifiers matching the given day for the given
 * object of modifiers.
 *
 * @export
 * @param {Date} day
 * @param {Record<string, Modifier | Array<Modifier>>} [modifiersObj={}]
 * @return {Array<string>}
 */
export function getModifiersForDay(
  day: Date,
  modifiersObj: Record<string, Modifier | Array<Modifier>> = {},
): Array<string> {
  return Object.keys(modifiersObj).reduce((modifiers: Array<string>, modifierName) => {
    const value = modifiersObj[modifierName];
    if (dayMatchesModifier(day, value)) {
      modifiers.push(modifierName);
    }
    return modifiers;
  }, []);
}

export default { dayMatchesModifier, getModifiersForDay };
